import {
  MoreVertical,
  ArrowUp,
  ArrowDown,
  Pencil,
  Trash2,
  Square,
  SquareX,
  SquareSlash,
  FlipVertical2,
  FolderMinus,
  Move3D,
  Waves,
  Spline,
  PenTool,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface LayerOverflowMenuProps {
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** True when this layer already has a mask attached. Drives
   *  Add Mask vs Delete Mask in the menu. */
  hasMask: boolean;
  /** Mask kind — 'raster' or 'vector'. Drives the Convert entry's
   *  target kind label and the Edit Vector Path entry's visibility.
   *  Tier 2 Group A PR-D. */
  maskKind: 'raster' | 'vector';
  /** True when this layer's mask is currently enabled. Reflected in
   *  the Disable / Enable menu label and the `mask.enabled` toggle. */
  maskEnabled: boolean;
  /** True when this layer's mask is currently inverted. Reflected
   *  in the Invert toggle's checked state. */
  maskInverted: boolean;
  /** True when there's a finalized marquee selection right now —
   *  Add Mask uses it to seed the mask alpha (selected = visible),
   *  matching Photoshop's "Add Vector Mask From Selection." */
  hasSelection: boolean;
  /** True when this row is a group node — surfaces the Ungroup
   *  item, hides Add Mask (groups can't carry masks themselves;
   *  mask their children instead). */
  isGroup: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRename: () => void;
  onDelete: () => void;
  onAddMask: () => void;
  /** Add a vector mask. Surfaces alongside Add Mask when the
   *  layer has no mask yet. Tier 2 Group A PR-D. */
  onAddVectorMask: () => void;
  onDeleteMask: () => void;
  /** Toggle the mask's `enabled` field — disabled masks render as
   *  fully-opaque alpha (layer unmasked) without losing the painted
   *  raw alpha. */
  onToggleMaskEnabled: () => void;
  /** Toggle the mask's `inverted` field — inverted masks render as
   *  255 - raw_alpha, swapping the hide / reveal regions. */
  onToggleMaskInverted: () => void;
  /** Convert the mask between raster and vector kinds. Tier 2
   *  Group A PR-D. The menu surfaces a single entry whose label
   *  flips based on current `maskKind`; calls the appropriate
   *  direction. EditorPage owns the trace / seed / re-rasterize
   *  flow. */
  onConvertMaskToVector: () => void;
  onConvertMaskToRaster: () => void;
  /** Enter Path Edit mode on a vector mask's path. Surfaced
   *  only when `maskKind === 'vector'`. Tier 2 Group A PR-D. */
  onEditVectorMaskPath: () => void;
  onUngroup: () => void;
  /** Free Transform — Tier 1 Group E. Opens the Transform mode
   *  on this layer. Hidden on group rows (groups don't have a
   *  single Fabric object to transform; would need a flatten-or-
   *  transform-recursively decision deferred). */
  onTransform: () => void;
  /** Liquify — Tier 1 Group E final piece. Opens the full-screen
   *  brush-based deformation workspace on this layer. Same hidden-
   *  on-group reasoning as Free Transform — Liquify needs a single
   *  rasterizable surface. */
  onLiquify: () => void;
  /** True when this row is a vector layer — surfaces the
   *  "Edit Path…" entry. Hidden on raster / group / adjustment
   *  rows (those have no path to edit). */
  isVector: boolean;
  /** Open Path Edit mode on this vector layer (Tier 2 Group A
   *  PR-B). No-op when `isVector` is false. */
  onEditPath: () => void;
}

/** Per-row overflow menu surfaced from the LayerRow's `⋮` icon.
 *  Tier 1 row redesign collapses the previously-inline up / down /
 *  trash buttons into this single trigger.
 *
 *  The roadmap calls for additional sections (Mask, Group,
 *  Clipping, Lock, Transform) once those features ship. Adding
 *  them later is a pure additive change to this file — no
 *  consumer-side wiring beyond the new prop callbacks. */
export function LayerOverflowMenu({
  canMoveUp,
  canMoveDown,
  hasMask,
  maskKind,
  maskEnabled,
  maskInverted,
  hasSelection,
  isGroup,
  onMoveUp,
  onMoveDown,
  onRename,
  onDelete,
  onAddMask,
  onAddVectorMask,
  onDeleteMask,
  onToggleMaskEnabled,
  onToggleMaskInverted,
  onConvertMaskToVector,
  onConvertMaskToRaster,
  onEditVectorMaskPath,
  onUngroup,
  onTransform,
  onLiquify,
  isVector,
  onEditPath,
}: LayerOverflowMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent shrink-0"
          aria-label="Layer options"
          title="Layer options"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel className="text-[10px] uppercase text-muted-foreground">
          Layer
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={onRename} className="text-xs">
          <Pencil className="h-3.5 w-3.5 mr-2" />
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onMoveUp} disabled={!canMoveUp} className="text-xs">
          <ArrowUp className="h-3.5 w-3.5 mr-2" />
          Move Up
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onMoveDown} disabled={!canMoveDown} className="text-xs">
          <ArrowDown className="h-3.5 w-3.5 mr-2" />
          Move Down
        </DropdownMenuItem>
        {!isGroup && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase text-muted-foreground">
              Mask
            </DropdownMenuLabel>
            {hasMask ? (
              <>
                <DropdownMenuItem onClick={onToggleMaskEnabled} className="text-xs">
                  <SquareSlash className="h-3.5 w-3.5 mr-2" />
                  {maskEnabled ? 'Disable Mask' : 'Enable Mask'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onToggleMaskInverted} className="text-xs">
                  <FlipVertical2 className="h-3.5 w-3.5 mr-2" />
                  {maskInverted ? 'Un-invert Mask' : 'Invert Mask'}
                </DropdownMenuItem>
                {maskKind === 'vector' ? (
                  <>
                    <DropdownMenuItem onClick={onEditVectorMaskPath} className="text-xs">
                      <Spline className="h-3.5 w-3.5 mr-2" />
                      Edit Vector Mask Path…
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onConvertMaskToRaster} className="text-xs">
                      <Square className="h-3.5 w-3.5 mr-2" />
                      Convert to Raster Mask
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem onClick={onConvertMaskToVector} className="text-xs">
                    <PenTool className="h-3.5 w-3.5 mr-2" />
                    Convert to Vector Mask
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={onDeleteMask} className="text-xs">
                  <SquareX className="h-3.5 w-3.5 mr-2" />
                  Delete Mask
                </DropdownMenuItem>
              </>
            ) : (
              <>
                <DropdownMenuItem onClick={onAddMask} className="text-xs">
                  <Square className="h-3.5 w-3.5 mr-2" />
                  {hasSelection ? 'Add Mask From Selection' : 'Add Mask'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onAddVectorMask} className="text-xs">
                  <PenTool className="h-3.5 w-3.5 mr-2" />
                  Add Vector Mask
                </DropdownMenuItem>
              </>
            )}
          </>
        )}
        {isGroup && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase text-muted-foreground">
              Group
            </DropdownMenuLabel>
            <DropdownMenuItem onClick={onUngroup} className="text-xs">
              <FolderMinus className="h-3.5 w-3.5 mr-2" />
              Ungroup
            </DropdownMenuItem>
          </>
        )}
        {!isGroup && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase text-muted-foreground">
              Transform
            </DropdownMenuLabel>
            <DropdownMenuItem onClick={onTransform} className="text-xs">
              <Move3D className="h-3.5 w-3.5 mr-2" />
              Free Transform…
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onLiquify} className="text-xs">
              <Waves className="h-3.5 w-3.5 mr-2" />
              Liquify…
            </DropdownMenuItem>
            {isVector && (
              <DropdownMenuItem onClick={onEditPath} className="text-xs">
                <Spline className="h-3.5 w-3.5 mr-2" />
                Edit Path…
              </DropdownMenuItem>
            )}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onDelete}
          className="text-xs text-destructive focus:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5 mr-2" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
